import { jsPDF } from "jspdf";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { CircularProgress, Grid, Icon } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";
import { gql, useLazyQuery } from "@apollo/client";
import EmpleadoLayout from "components/LayoutContainers/EmpleadoLayout";
import boxShadows from "assets/theme/base/boxShadows";
import PdfHeader from "../components/pdfHeader";
import { formatMoneyPunto } from "utils/formatters";
import { formatDate } from "utils/formatters";
import { Money2Text } from "utils/formatters";
import { fromBaseQuantity } from "utils/provisionQuantity";
import { nombreCompraDetalle } from "utils/presentacionCompra";

const tipoDocumentos = [
	{ id: 80, label: "CUIT" },
	{ id: 86, label: "CUIL" },
	{ id: 96, label: "DNI" },
];

const QUANTITY_INTEGER_THRESHOLD = 0.09;

const formatDeliveredQuantity = (value) => {
	const numericValue = Number(value) || 0;
	const nearestInteger = Math.round(numericValue);
	const decimalDelta = Math.abs(numericValue - nearestInteger);

	if (decimalDelta <= QUANTITY_INTEGER_THRESHOLD) {
		return nearestInteger;
	}

	return Number(numericValue.toFixed(2)).toLocaleString("es-AR", {
		minimumFractionDigits: 1,
		maximumFractionDigits: 2,
	});
};

const RemitoIndividual = () => {
	const documentsRef = useRef(null);
	const { id } = useParams(); // ID del remito (ingreso)
	const { lg } = boxShadows;
	const [data, setData] = useState(null);
	const [getRemito, { loading, error }] = useLazyQuery(
		gql`
      query getRemitoDocumento($id: ID!) {
        remitoDocumento(id: $id) {
          id
          tipo
          fechaEntrega
          observaciones
          createdAt
          provision {
            id
            pedidoCompra {
              id
              numeroExpediente
              tipoCompra
              domicilioEntrega
              fechaEntrega
            }
            proveedor {
              id
              tipoProveedor
              razonSocial
              nombre
              apellido
              tipoDocumento
              documento
              user {
                email
              }
            }
            detalles {
              id
              materiaPrima {
                nombre
                nombreCompra
                tipoUnidad {
                  nombre
                  multiplicador
                }
              }
              pedidoCompraDetalle {
                numero
                nombreCompra
                unidadNombre
                nombreServicio
                requierePack
                unidadPack
                totalMP
                cantidad
                equivalenciaPack {
                  materiaPrima
                  packs
                }
              }
              precioUnitario
              cantidad
            }
          }
          movimientoDetalles {
            id
            cantidad
            materiaPrima {
              nombre
              nombreCompra
              tipoUnidad {
                nombre
                multiplicador
              }
            }
            idProvisionDetalle
          }
        }
      }
    `,
		{
			onCompleted: (result) => {
				setData(result?.remitoDocumento ?? null);
			},
			onError: (error) => {
				console.error("Error al cargar remito:", error);
			},
		}
	);

	useEffect(() => {
		setData(null);
		if (id) {
			getRemito({ variables: { id } });
		}
	}, [id, getRemito]);

	// Calcular el total del remito (solo productos entregados en este remito)
	const totalRemitoCalculado = data?.movimientoDetalles?.reduce((acc, detalle) => {
		const provisionDetalle = data?.provision?.detalles?.find(
			pd => pd.id === detalle.idProvisionDetalle
		);


		const pcd = provisionDetalle?.pedidoCompraDetalle;
		const mp = detalle.materiaPrima;
		const cantidadBase = fromBaseQuantity({
			quantityBase: Number(detalle.cantidad) || 0,
			multiplicador: mp?.tipoUnidad?.multiplicador,
			pedidoCompraDetalle: pcd,
		});

		const precioUnit = provisionDetalle?.precioUnitario ?? 0;
		const total = cantidadBase * precioUnit;

		return acc + total;
	}, 0) || 0;
	const totalRemito = totalRemitoCalculado;

	const generateWaterMark = () => {
		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");
		canvas.width = 400;
		canvas.height = 400;
		ctx.font = "80px Arial";
		ctx.fillStyle = "rgba(255, 0, 0, 0.2)";
		ctx.textAlign = "center";
		ctx.translate(canvas.width / 2, canvas.height / 2);
		ctx.rotate((-45 * Math.PI) / 180);
		ctx.translate(-canvas.width / 2, -canvas.height / 2);
		ctx.fillText("ANULADA", canvas.width / 2, canvas.height / 2);
		return canvas.toDataURL();
	};

	const handleGeneratePdf = async () => {
		const doc = new jsPDF({
			format: "a4",
			unit: "px",
			orientation: "portrait",
			hotfixes: ["px_scaling"],
			compress: true,
		});

		const pdfContent = documentsRef.current;

		await doc.html(pdfContent, {
			x: 20,
			y: 20,
			width: 754,
			windowWidth: 800,
			margin: [40, 10, 40, 10],
			autoPaging: "text",
			html2canvas: {
				scale: 0.7,
				useCORS: true,
				scrollX: 0,
				scrollY: 0,
				width: 794,
				height: pdfContent.scrollHeight,
			},
			callback: function (doc) {
				doc.save(`remito-${data.id}-OP-${data.provision.id}.pdf`);
			},
		});
	};

	if (loading) {
		return (
			<EmpleadoLayout>
				<SoftBox
					display="flex"
					justifyContent="center"
					alignItems="center"
					height="100vh"
					flexDirection="column"
					gap={3}
				>
					<CircularProgress size={40} color="primary" />
					<SoftTypography variant="h5">Cargando remito...</SoftTypography>
				</SoftBox>
			</EmpleadoLayout>
		);
	}

	if (!data) {
		return (
			<EmpleadoLayout>
				<SoftBox
					display="flex"
					justifyContent="center"
					alignItems="center"
					height="100vh"
					flexDirection="column"
					gap={3}
				>
					<Icon fontSize="large">search_off</Icon>
					<SoftTypography variant="h5">
						{error ? "No se pudo cargar el remito" : "Remito no disponible"}
					</SoftTypography>
					<SoftTypography variant="body2">
						{error
							? "Verificá tu acceso e intentá nuevamente."
							: "El remito no existe o no está disponible para tu usuario."}
					</SoftTypography>
					<SoftTypography variant="body2">ID buscado: {id}</SoftTypography>
				</SoftBox>
			</EmpleadoLayout>
		);
	}

	return (
		<EmpleadoLayout>
			<SoftBox
				p={2}
				display="flex"
				alignItems="center"
				flexDirection="column"
				minHeight="100vh"
				height="100%"
				gap={4}
			>
				{/* Contenido del PDF */}
				<div ref={documentsRef}>
					<SoftBox
						sx={{
							position: "relative",
							overflow: "hidden",
							width: "794px",
							backgroundColor: "#ffffff",
							boxShadow: lg,
							px: 6,
						}}
						pb={5}
					>
						<PdfHeader />
						{data.anulada && (
							<img
								src={generateWaterMark()}
								alt="anulada"
								style={{
									position: "absolute",
									top: "25%",
									left: "25%",
									zIndex: 1000,
									width: "500px",
									height: "500px",
									pointerEvents: "none",
								}}
							/>
						)}
						<SoftBox display="flex" flexDirection="column" gap={2} lineHeight={2} fontSize="12px">
							{/* Encabezado con número de remito y total */}
							<Grid container border="1pt solid black">
								<Grid item xs={8} pl={1} fontWeight="bold">
									<div>REMITO N° {data.id}</div>
									<div>ORDEN DE PROVISIÓN N° {data.provision?.id}</div>
									{data.anulada && (
										<>
											<span style={{ color: "red" }}>ANULADA: </span>
											<span style={{ fontWeight: "normal" }}>{data.motivoAnulacion}</span>
										</>
									)}
								</Grid>
								<Grid item xs={4} textAlign="center" borderLeft="1pt solid black">
									<SoftBox borderBottom="1pt solid black">Monto de este Remito</SoftBox>
									<SoftBox fontWeight="bold">$ {formatMoneyPunto(totalRemito)}</SoftBox>
								</Grid>
							</Grid>

							{/* Datos generales */}
							<Grid
								container
								border="1pt solid black"
								lineHeight={1.5}
								sx={{ '& > *:not(:nth-last-of-type(-n+2))': { borderBottom: "1pt solid black" } }}
							>
								<Grid item xs={3} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
									Fecha de Remito
								</Grid>
								<Grid item xs={3} px={1}>
									{formatDate(data.fechaEntrega || data.createdAt, "dd/MM/yyyy")}
								</Grid>
								<Grid item xs={3} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
									Tipo de Contratación
								</Grid>
								<Grid item xs={3} px={1} borderRight="1pt solid black">
									{data.provision?.pedidoCompra?.tipoCompra?.toUpperCase() ?? "-"}
								</Grid>
								<Grid item xs={3} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
									Fecha de Entrega Original
								</Grid>
								<Grid item xs={3} px={1}>
									{formatDate(data.provision?.pedidoCompra?.fechaEntrega, "dd/MM/yyyy")}
								</Grid>
								<Grid item xs={6} />
								<Grid item xs={3} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
									Domicilio de Entrega
								</Grid>
								<Grid item xs={9} px={1}>{data.provision?.pedidoCompra?.domicilioEntrega}</Grid>
								<Grid item xs={3} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
									Contacto
								</Grid>
								<Grid item xs={9} px={1}>comedorupro@uprosanluis.edu.ar</Grid>
							</Grid>

							{/* Proveedor */}
							<Grid
								container
								border="1pt solid black"
								sx={{ '& > *:not(:nth-last-of-type(-n+1))': { borderBottom: "1pt solid black" } }}
							>
								<Grid item xs={12} backgroundColor="#f0f0f0" fontWeight="bold" px={1}>PROVEEDOR</Grid>
								<Grid item xs={6} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
									{data.provision?.proveedor?.tipoProveedor === "Jurídica"
										? "Razón Social"
										: "Apellido y Nombre"}
								</Grid>
								<Grid item xs={6} px={1}>
									{data.provision?.proveedor?.razonSocial ??
										`${data.provision?.proveedor?.apellido}, ${data.provision?.proveedor?.nombre}`}
								</Grid>
								<Grid item xs={6} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
									{tipoDocumentos.find(e => e.id === data.provision?.proveedor?.tipoDocumento)?.label || 'Documento'}
								</Grid>
								<Grid item xs={6} px={1}>{data.provision?.proveedor?.documento ?? "-"}</Grid>
								<Grid item xs={6} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
									Email
								</Grid>
								<Grid item xs={6} px={1}>
									{data.provision?.proveedor?.user?.email ?? "-"}
								</Grid>
								<Grid item xs={12} pl={1}>
									Sírvase efectuar y/o proveer a:{" "}
									<span style={{ fontWeight: "bold" }}>
										FUNDACIÓN UPrO
									</span>
								</Grid>
							</Grid>

							{/* Productos entregados */}
							<Grid
								container
								border="1pt solid black"
								sx={{ '& > *:not(:last-child)': { borderBottom: "1pt solid black" } }}
							>
								<Grid item xs={12} backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
									PRODUCTOS ENTREGADOS EN ESTE REMITO
								</Grid>
								<Grid
									item
									container
									xs={12}
									backgroundColor="#f0f0f0"
									fontWeight="bold"
									sx={{ '& > *:not(:last-child)': { borderRight: "1pt solid black" } }}
								>
									<Grid item xs={0.8} px={1} textAlign="center">Item</Grid>
									<Grid item xs={4} px={1}>Descripción</Grid>
									<Grid item xs={1.5} px={1} textAlign="center">Cantidad</Grid>
									<Grid item xs={1.5} px={1} textAlign="center">Unidad</Grid>
									<Grid item xs={2} px={1} textAlign="center">Precio unitario</Grid>
									<Grid item xs={2} px={1} textAlign="center">Precio total</Grid>
								</Grid>
								{data?.movimientoDetalles?.map((detalle, index) => {
									const provisionDetalle =
										data?.provision?.detalles?.find(pd => pd.id === detalle.idProvisionDetalle);

									const pcd = provisionDetalle?.pedidoCompraDetalle;
									const mp = detalle.materiaPrima;
									const cantidadRecibida = fromBaseQuantity({
										quantityBase: Number(detalle.cantidad) || 0,
										multiplicador: mp?.tipoUnidad?.multiplicador,
										pedidoCompraDetalle: pcd,
									});

									const precioUnit = provisionDetalle?.precioUnitario ?? 0;
									const total = cantidadRecibida * precioUnit;

									return (
										<Grid
											key={detalle.id || index}
											lineHeight={1.5}
											item
											container
											xs={12}
											sx={{ '& > *:not(:last-child)': { borderRight: "1pt solid black" } }}
										>
											<Grid item xs={0.8} px={1} textAlign="center">
												{pcd?.numero || index + 1}
											</Grid>

											<Grid item xs={4} px={1}>
                                                {nombreCompraDetalle(pcd, provisionDetalle?.materiaPrima || mp)}
											</Grid>

											<Grid item xs={1.5} px={1} textAlign="center">
												{formatDeliveredQuantity(cantidadRecibida)}
											</Grid>

											<Grid item xs={1.5} px={1} textAlign="center">
												{pcd?.requierePack
													? pcd?.unidadPack
                                                    : pcd?.unidadNombre || mp?.tipoUnidad?.nombre || "-"}
											</Grid>

											<Grid item xs={2} px={1} textAlign="right">
												$ {formatMoneyPunto(precioUnit)}
											</Grid>

											<Grid item xs={2} px={1} textAlign="right">
												$ {formatMoneyPunto(total)}
											</Grid>
										</Grid>
									);
								})}

							</Grid>

							{/* Total */}
							<SoftBox border="1px solid black">
								<SoftBox
									px={1}
									borderBottom="1px solid black"
									bgColor="#f0f0f0"
									display="flex"
									justifyContent="space-between"
									fontWeight="bold"
								>
									<div>TOTAL DE ESTE REMITO:</div>
									<div>$ {formatMoneyPunto(totalRemito)}</div>
								</SoftBox>
								<SoftBox px={1}>{Money2Text(totalRemito).toUpperCase()}</SoftBox>
							</SoftBox>

							{/* Observaciones */}
							{data.observaciones && (
								<SoftBox border="1px solid black" px={1} py={1}>
									<SoftBox fontWeight="bold" mb={1}>OBSERVACIONES DEL REMITO:</SoftBox>
									<SoftBox>{data.observaciones}</SoftBox>
								</SoftBox>
							)}

							{/* Instrucciones */}
							{/* <SoftTypography variant="caption">
								Una vez realizada la entrega, solicitamos que envíe al siguiente correo: pagoaproveedores@uprosanluis.edu.ar la siguiente documentación:
							</SoftTypography> */}
							<ul style={{ lineHeight: 1.5, marginLeft: 12 }}>
								<li><strong>Sírvase efectuar y/o proveer a</strong>: FUNDACIÓN UPRO</li>
								<li><strong> DATOS DE FACTURACIÓN</strong>: Emitir Factura Original a nombre de FUNDACIÓN UPRO – CUIT 30-71886198-1 – Responsable Inscripto. La factura deberá detallar los ítems facturados, conforme a la correspondiente Orden de Provisión y/o Remito.</li>
								<li> Para consultas relacionadas con facturación y/o pagos pendientes, comunicarse al correo electrónico: <em><a href="#">comedorupro@uprosanluis.edu.ar</a></em>.</li>
							</ul>
						</SoftBox>
					</SoftBox>
				</div>

				{/* Botón flotante para descargar PDF */}
				<SoftBox
					variant="contained"
					bgColor="white"
					p={2}
					style={{
						position: "fixed",
						bottom: "1.5rem",
						right: "1.5rem",
						zIndex: 1000,
						boxShadow:
							"inset 0rem 0rem 0.0625rem 0.0625rem rgb(255 255 255 / 90%), 0rem 1.25rem 1.6875rem 0rem rgb(0 0 0 / 5%)",
						borderRadius: "16px",
						backgroundColor: "rgba(255, 255, 255, 0.8)",
						backdropFilter: `saturate(200%) blur(${pxToRem(30)})`,
					}}
				>
					<Grid container spacing={2} justifyContent="flex-end">
						<Grid item>
							<SoftButton variant="gradient" color="error" onClick={handleGeneratePdf}>
								<Icon fontWeight="bold">picture_as_pdf</Icon>
								&nbsp;&nbsp;Descargar PDF
							</SoftButton>
						</Grid>
					</Grid>
				</SoftBox>
			</SoftBox>
		</EmpleadoLayout>
	);
};

export default RemitoIndividual;



